import { type ModalProps } from '~/util';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '~/primitives/sheet';
import { useAppForm } from '~/util/hooks/useAppForm';
import { formOptions } from '@tanstack/react-form';
import { useList } from '~/util/hooks/useList';
import { useApiMutation } from '~/util/hooks/useApiMutation';

import { useEffect, useState } from 'react';
import { packFormInput, packFormSchema } from '~/util/forms/packtier';
import {
    deletePackMutation,
    updatePackMutation,
} from '~/util/mutations/staff/packs';
import { Pack, PackTier } from '~/util/types/packs';
import { PacksTierTags } from '~/partials/Tags';
import { Button } from '~/primitives/button';
import ConfirmModal from '~/components/ConfirmModal';
import { Trash } from 'lucide-react';

export const EditPackSheet: React.FC<
    {
        packtier: PackTier;
        pack: Pack;
    } & ModalProps
> = ({ packtier, pack, ...modalProps }) => {
    const list = useList();

    const mutation = useApiMutation({
        ...updatePackMutation(),
        onSuccess: () => modalProps.onOpenChange?.(false),
    });

    const deleteMutation = useApiMutation({
        ...deletePackMutation(),
        onSuccess: () => modalProps.onOpenChange?.(false),
    });

    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const form = useAppForm(
        formOptions({
            formId: 'edit-pack-staff',
            defaultValues: {
                list,
                name: pack.name,
                levels: pack.levels,
                tier: packtier.id,
            } as packFormInput,
            validators: {
                onChangeAsyncDebounceMs: 500,
                onChange: packFormSchema,
            },
            onSubmit: async ({ value }) => {
                mutation.mutate({ data: value, id: pack.id });
            },
        }),
    );

    useEffect(() => {
        form.reset({
            list,
            name: pack.name,
            levels: pack.levels,
            tier: packtier.id,
        });
    }, [list, packtier, pack, form]);

    return (
        <>
            <Sheet {...modalProps}>
                <SheetContent className="w-full sm:w-auto sm:min-w-150 sm:max-w-150 overflow-y-scroll">
                    <SheetHeader>
                        <SheetTitle>Edit Pack</SheetTitle>
                        <SheetDescription>
                            Update pack details and included levels.
                        </SheetDescription>
                    </SheetHeader>

                    <form
                        id="edit-pack-staff"
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.handleSubmit();
                        }}
                    >
                        <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                            <PacksTierTags item={packtier} />

                            <form.AppField name="name">
                                {(field) => (
                                    <field.TextField
                                        label="Name"
                                        required
                                        placeholder="Name of the pack"
                                    />
                                )}
                            </form.AppField>

                            <form.AppField name="levels">
                                {(field) => (
                                    <field.LevelsField
                                        list={list}
                                        label="Levels"
                                        required
                                    />
                                )}
                            </form.AppField>
                        </div>

                        <SheetFooter className="sm:justify-center">
                            <div className="flex flex-col w-full gap-2">
                                <form.AppForm>
                                    <form.SubscribeButton
                                        label="Edit Pack"
                                        classname="w-full"
                                    />
                                </form.AppForm>
                                <Button
                                    variant="destructive"
                                    className="w-1/3 self-end"
                                    type="button"
                                    onClick={() => setIsConfirmOpen(true)}
                                >
                                    <Trash className="h-8 w-8" /> Delete pack
                                </Button>
                            </div>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>
            <ConfirmModal
                title="Delete Pack"
                text="Are you sure you want to delete this pack? This action cannot be undone."
                buttonText="Delete Pack"
                onConfirm={() => deleteMutation.mutate({ id: pack.id, list })}
                buttonVariant="destructive"
                onOpenChange={setIsConfirmOpen}
                open={isConfirmOpen}
            />
        </>
    );
};
